<div class="h-full flex flex-col overflow-hidden">
  <!-- Form Header -->
  <div class="border-b border-zinc-200 dark:border-zinc-700 px-6 py-4 flex-none bg-zinc-50/50 dark:bg-zinc-800/50">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          {{ getHeaderTitle() }}
        </h2>
        <p class="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
          {{ getHeaderSubtitle() }}
        </p>
      </div>
      <!-- Step Indicator -->
      <div class="flex items-center gap-2">
        <button
          data-testid="template-form-step1-indicator"
          type="button"
          (click)="previousStep()"
          [disabled]="currentStep() === 1"
          [attr.aria-label]="currentStep() === 2 ? 'Back to step 1: Details' : null"
          class="flex items-center gap-1.5 rounded-md disabled:cursor-default"
          [class.cursor-pointer]="currentStep() === 2"
        >
          <div class="flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold"
               [class]="currentStep() >= 1 ? 'bg-blue-600 text-white' : 'bg-zinc-200 dark:bg-zinc-600 text-zinc-500 dark:text-zinc-400'">
            1
          </div>
          <span class="text-xs font-medium"
                [class]="currentStep() >= 1 ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'">Details</span>
        </button>
        <div class="w-8 h-px bg-zinc-300 dark:bg-zinc-600"></div>
        <div class="flex items-center gap-1.5">
          <div class="flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold"
               [class]="currentStep() >= 2 ? 'bg-blue-600 text-white' : 'bg-zinc-200 dark:bg-zinc-600 text-zinc-500 dark:text-zinc-400'">
            2
          </div>
          <span class="text-xs font-medium"
                [class]="currentStep() >= 2 ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'">Structure</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Body: Form + Anchor Fields Sidebar -->
  <div class="flex-1 flex min-h-0 overflow-hidden">
  <form [formGroup]="templateForm" (ngSubmit)="onSubmit()" class="flex-1 flex flex-col min-h-0 overflow-hidden">
    <!-- Step 1: Metadata -->
    <div *ngIf="currentStep() === 1" formGroupName="metadata" class="flex-1 overflow-y-auto p-6">
      <div class="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Left Column: Form Fields -->
        <div class="space-y-6">
          <app-template-metadata-fields [metadataForm]="asFormGroup(templateForm.get('metadata'))"
                                        [isExtracting]="isExtractingFields()"></app-template-metadata-fields>

          <!-- Anchors are extracted at leaf assignment on the Templates page (not here). -->
          <div class="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 px-4 py-3 text-xs text-zinc-500 dark:text-zinc-400"
               data-testid="anchors-info-note">
            Anchor values are extracted when the template is assigned to an anchor-tree leaf on the Templates page.
          </div>

          <div>
            <label for="file" class="block text-sm font-medium mb-1.5 text-zinc-700 dark:text-zinc-300">
              Sample Document
              @if (!isEditMode()) {
                <span class="text-red-500">*</span>
              }
            </label>

            <!-- File upload for new templates only -->
            @if (!isEditMode()) {
              <div
                class="border-2 border-dashed border-zinc-300 dark:border-zinc-600 rounded-xl p-8 text-center hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:border-blue-400 transition-all duration-200 relative"
                [ngClass]="{ 'border-blue-500 bg-blue-50 dark:bg-blue-900/20': isDragging() }"
                (dragover)="onDragOver($event)"
                (dragenter)="onDragEnter($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)">
                <input
                  data-testid="template-form-file-input"
                  id="file-upload"
                  type="file"
                  (change)="onFileSelected($event).then()"
                  accept=".pdf"
                  class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  [disabled]="isExtractingFields()"
                >
                <div class="flex flex-col items-center gap-2">
                  <div class="p-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                    </svg>
                  </div>
                  @if (selectedFile) {
                    <div class="text-sm font-medium text-green-600 flex items-center gap-1">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      {{ selectedFile.name }}
                    </div>
                  } @else {
                    <p class="text-sm font-medium text-zinc-900 dark:text-zinc-100">Click to upload or drag and drop</p>
                    <p class="text-xs text-zinc-500 dark:text-zinc-400">PDF up to 10MB</p>
                  }
                </div>
              </div>
              @if (templateForm.get('metadata.file')?.invalid && templateForm.get('metadata.file')?.touched) {
                <p data-testid="template-form-file-error" class="mt-1.5 text-xs text-red-600">Please upload a sample document</p>
              }
            }

            <!-- Read-only file info for edit mode -->
            @if (isEditMode()) {
              <div class="mt-1 p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700">
                <div class="flex items-center gap-3">
                  <div class="p-2 bg-zinc-100 dark:bg-zinc-700 rounded-lg">
                    <svg class="h-5 w-5 text-zinc-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-zinc-700 dark:text-zinc-300">Template file cannot be changed</p>
                    <p class="text-xs text-zinc-500 dark:text-zinc-400">The original file is preserved when editing a template</p>
                  </div>
                </div>
              </div>
            }

            <!-- Loading Indicator for Field Extraction -->
            @if (isExtractingFields()) {
              <div class="mt-3 flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
                <div class="animate-spin rounded-full h-4 w-4 border-2 border-zinc-200 dark:border-zinc-600 border-t-blue-600"></div>
                Extracting fields...
              </div>
            }

            <!-- Error Message and Retry Button for Field Extraction -->
            @if (fieldExtractionError()) {
              <div class="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p class="text-sm text-red-700 dark:text-red-300">{{ fieldExtractionError() }}</p>
                <button
                  data-testid="template-form-retry-extraction-btn"
                  type="button"
                  (click)="retryFieldExtraction()"
                  class="mt-2 inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            }
          </div>
        </div>

        <!-- Right Column: PDF Preview -->
        <div class="space-y-6">
          <app-pdf-preview
            [filePreviewUrl]="filePreviewUrl"
            [selectedFile]="selectedFile"
            [isEditMode]="isEditMode()"
          ></app-pdf-preview>
        </div>
      </div>
    </div>

    <!-- Step 2: Structure -->
    @if (currentStep() === 2) {
      <app-resizable-container [initialPercent]="50" class="flex-1 min-h-0 p-6">
        <div #fieldsContainer left class="h-full overflow-y-auto pr-4">
          <!-- Fields Header -->
          <div
            class="flex justify-between items-center sticky top-0 bg-white dark:bg-zinc-800 z-20 py-3 px-1 border-b border-zinc-100 dark:border-zinc-700 mb-4">
            <div>
              <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Field Annotations</h3>
              <p class="text-xs text-zinc-500 dark:text-zinc-400">Verify extracted values against the document.</p>
            </div>
            <div class="flex items-center gap-2">
            <span
              class="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-700/10 dark:ring-blue-400/20">
              {{ structureFields.length }} Fields
            </span>
            </div>
          </div>

          <!-- Toolbar -->
          <div class="flex flex-wrap items-center gap-3 mb-4">
            @if (selectedFile || isEditMode()) {
              <button
                data-testid="template-form-re-extract-btn"
                type="button"
                (click)="retriggerFieldExtraction()"
                [disabled]="isExtractingFields()"
                class="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-xs font-medium text-zinc-700 dark:text-zinc-300 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                matTooltip="Re-extract field values from the document"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Re-extract
              </button>

              <div class="flex items-center gap-2 border-l border-zinc-200 dark:border-zinc-600 pl-3">
                <mat-slide-toggle
                  data-testid="template-form-override-values-toggle"
                  [checked]="overrideExistingValues()"
                  (change)="overrideExistingValues.set($event.checked)"
                  [disabled]="isExtractingFields()"
                  class="!text-xs"
                >
                  <span class="text-xs text-zinc-600 dark:text-zinc-400"
                        [matTooltip]="'When enabled, re-extraction will replace all existing example values with newly extracted ones. When disabled, fields that already have a value will keep it. Descriptions are always preserved.'"
                  >Override values</span>
                </mat-slide-toggle>
              </div>
            }
          </div>

          @if (selectedFile || isEditMode()) {
            <div class="flex items-start gap-2 mb-4 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <svg class="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p class="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">
                Re-extraction runs AI on the document to update example values. Descriptions and field structure are
                always kept.
                @if (!overrideExistingValues()) {
                  <strong>Override is off</strong> — fields with existing values will not be changed.
                } @else {
                  <strong>Override is on</strong> — all example values will be replaced with newly extracted ones.
                }
              </p>
            </div>
          }

          <!-- Loading Indicator for Field Extraction (Visible in Step 2) -->
          @if (isExtractingFields()) {
            <div
              class="flex items-center justify-center gap-3 text-sm text-zinc-500 dark:text-zinc-400 p-6 bg-zinc-50 dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700">
              <div class="animate-spin rounded-full h-5 w-5 border-2 border-zinc-200 dark:border-zinc-600 border-t-blue-600"></div>
              Extracting fields from the document...
            </div>
          }

          <!-- Error Message and Retry Button for Field Extraction (Visible in Step 2) -->
          @if (fieldExtractionError() && !isExtractingFields()) {
            <div class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <div class="flex items-start gap-3">
                <svg class="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor"
                     viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <div>
                  <p class="text-sm font-medium text-red-800 dark:text-red-300">Error Extracting Fields</p>
                  <p class="text-sm text-red-600 dark:text-red-400 mt-1">{{ fieldExtractionError() }}</p>
                  <button
                    data-testid="template-form-retry-extraction-step2-btn"
                    type="button"
                    (click)="retryFieldExtraction()"
                    class="mt-2 inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors"
                  >
                    Retry Extraction
                  </button>
                </div>
              </div>
            </div>
          }

          @if (!isExtractingFields() && !fieldExtractionError()) {
            <div
              cdkDropList
              [cdkDropListData]="structureFields.controls"
              (cdkDropListDropped)="drop($event)"
              formArrayName="structure"
              class="space-y-2"
            >
              @for (field of structureFields.controls; track field; let i = $index) {
                @if (getFieldDefinition(asFormGroup(field).get('fieldId')?.value); as fieldDef) {
                  <app-template-field-editor
                    class="block"
                    cdkDrag
                    [fieldForm]="asFormGroup(field)"
                    [index]="i"
                    [isExpanded]="expandedFieldIndex() === i"
                    [fieldDefinition]="fieldDef"
                    (onToggleExpansion)="toggleFieldExpansion(i)"
                    (editField)="onEditFieldDefinition($event)"
                  ></app-template-field-editor>
                } @else {
                  <div class="block">
                    <div class="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg border border-red-400 dark:border-red-600">
                      <p class="text-sm text-zinc-700 dark:text-zinc-300">Field {{ asFormGroup(field).get('fieldId')?.value }} is missing from the structure</p>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400">This field will be removed from the template</p>
                    </div>
                  </div>
                }
              }

              @if (structureFields.length === 0 && !isExtractingFields() && !fieldExtractionError()) {
                <div class="text-center py-12 px-4">
                  <div class="mx-auto h-12 w-12 rounded-full bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center mb-3">
                    <svg class="h-6 w-6 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"/>
                    </svg>
                  </div>
                  <h3 class="text-sm font-medium text-zinc-900 dark:text-zinc-100">No fields to annotate</h3>
                  <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-1 max-w-[220px] mx-auto">Fields will be extracted after uploading a
                    document, or you can add them manually.</p>
                </div>
              }
            </div>
          }
        </div>

        <div right class="h-full overflow-hidden">
          <app-pdf-preview
            [filePreviewUrl]="filePreviewUrl"
            [selectedFile]="selectedFile"
            [isEditMode]="isEditMode()"
            height="100%"
            label="Document Preview"
            containerClass="h-full"
          ></app-pdf-preview>
        </div>
      </app-resizable-container>
    }

    <!-- Form Actions -->
    <div class="px-6 py-4 flex justify-between items-center border-t border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 flex-none">
      <button
        data-testid="template-form-cancel-btn"
        type="button"
        (click)="cancel()"
        class="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 transition-colors"
      >
        Cancel
      </button>

      <div class="flex gap-3">
        @if (currentStep() === 2) {
          <button
            data-testid="template-form-back-btn"
            type="button"
            (click)="previousStep()"
            class="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 transition-colors"
          >
            Back
          </button>
        }

        @if (currentStep() === 1) {
          <button
            data-testid="template-form-next-step-btn"
            type="button"
            (click)="nextStep()"
            class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 cursor-pointer transition-colors"
          >
            Next Step
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        }

        @if (currentStep() === 2) {
          <button
            data-testid="template-form-submit-btn"
            type="submit"
            [disabled]="!templateForm.valid || isSubmitting() || isExtractingFields()"
            class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            @if (isSubmitting()) {
              <div class="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
              <span>{{ isEditMode() ? 'Updating...' : 'Submitting...' }}</span>
            } @else {
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              <span>{{ isEditMode() ? 'Update Template' : 'Save Template' }}</span>
            }
          </button>
        }
      </div>
    </div>
  </form>

    @if (currentStep() === 1) {
      <app-anchor-fields-sidebar class="hidden lg:block flex-none"></app-anchor-fields-sidebar>
    }
  </div>
</div>
